import { useEffect, useState } from "react";
import { RefreshCw, X } from "lucide-react";
import problemService from "../services/ProblemService";
import { AdminLayout } from "../components/layout/AdminLayout";
import TableSkeleton from "../components/ui/table-skeleton";
import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "../components/ui/paginator/pagination";
import { useToast } from "../contexts/ToastContext";

// Define the Problem interface based on the response structure
interface Problem {
    id: number;
    user_id: number;
    description: string;
    type: string;
    status: string;
    created_at: string;
    updated_at: string;
    user: {
        id: number;
        name: string;
        email: string;
        phone: string;
        role: string;
        profile: string;
        username: string;
        status: boolean;
        created_at: string;
        updated_at: string;
    };
}

// Modal component for viewing problem details
const ProblemDetailsModal = ({ 
    problem, 
    isOpen, 
    onClose 
}: { 
    problem: Problem | null; 
    isOpen: boolean; 
    onClose: () => void;
}) => {
    if (!isOpen || !problem) return null;

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadgeClass = (status: string) => {
        switch (status.toLowerCase()) {
            case 'open':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
            case 'in progress':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
            case 'resolved':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'closed':
                return 'bg-neutral-100 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-200';
            default:
                return 'bg-neutral-100 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-200';
        }
    };

    const basUrlStorage = process.env.REACT_APP_STORAGE_URL + "/";

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="relative w-full max-w-2xl p-6 mx-4 bg-white dark:bg-neutral-800 rounded-lg shadow-xl">
                <button 
                    onClick={onClose}
                    className="absolute top-4 right-4 p-1 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-700"
                >
                    <X size={20} className="text-neutral-500 dark:text-neutral-400" />
                </button>
                
                <h2 className="text-xl font-semibold mb-4 text-neutral-900 dark:text-neutral-100">
                    Problème #{problem.id} Details
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                        <h3 className="text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-1">Statut</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(problem.status)}`}>
                            {problem.status}
                        </span>
                    </div>
                    
                    <div>
                        <h3 className="text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-1">Type</h3>
                        <p className="text-neutral-900 dark:text-neutral-200 capitalize">{problem.type}</p>
                    </div>
                    
                    <div>
                        <h3 className="text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-1">Date de signalement</h3>
                        <p className="text-neutral-900 dark:text-neutral-200">{formatDate(problem.created_at)}</p>
                    </div>
                    
                    <div>
                        <h3 className="text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-1">Dernière mise à jour</h3>
                        <p className="text-neutral-900 dark:text-neutral-200">{formatDate(problem.updated_at)}</p>
                    </div>
                </div>
                
                <div className="mb-6">
                    <h3 className="text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-2">Signalé par</h3>
                    <div className="flex items-center">
                        {problem.user.profile && (
                            <img 
                                src={`${basUrlStorage + problem.user.profile}`}
                                alt={problem.user.name} 
                                className="w-10 h-10 rounded-full mr-3"
                            />
                        )}
                        <div>
                            <p className="text-neutral-900 dark:text-neutral-100 font-medium">{problem.user.name}</p>
                            <p className="text-neutral-500 dark:text-neutral-400 text-sm">{problem.user.email}</p>
                            <p className="text-neutral-500 dark:text-neutral-400 text-sm">{problem.user.phone}</p>
                        </div>
                    </div>
                </div>
                
                <div>
                    <h3 className="text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-2">Description</h3>
                    <div className="p-4 bg-neutral-50 dark:bg-neutral-700 rounded-lg text-neutral-900 dark:text-neutral-200 whitespace-pre-wrap">
                        {problem.description}
                    </div>
                </div>
            </div>
        </div>
    );
};

export const ProblemsPage = () => {
    // State
    const [problems, setProblems] = useState<Problem[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedProblem, setSelectedProblem] = useState<Problem | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    
    const basUrlStorage = process.env.REACT_APP_STORAGE_URL + "/";
    const { showToast } = useToast();

    // Fetch problems on component mount
    useEffect(() => {
        fetchProblems();
    }, [currentPage]);

    const fetchProblems = async () => {
        setLoading(true);
        try {
            const response = await problemService.getProblems();
            if (response.data) {
                setProblems(response.data);
                setTotalPages(Math.ceil(response.data.length / 10));
            }
        } catch (error) {
                        showToast("error", "Erreur lors du chargement des problèmes");
        } finally {
            setLoading(false);
        }
    };

    // Handle status change
    const handleStatusChange = async (problemId: number, newStatus: string) => {
        try {
            await problemService.updateProblemStatus(problemId, newStatus);
            
            // Update the local state to reflect the change
            setProblems(problems.map(problem => 
                problem.id === problemId 
                    ? { ...problem, status: newStatus } 
                    : problem
            ));
            
            showToast("success", "Problème mis à jour avec succès");
        } catch (error) {
                        showToast("error", "Échec de la mise à jour du problème");
        }
    };

    // Open problem details modal
    const handleViewDetails = (problem: Problem) => {
        setSelectedProblem(problem);
        setIsModalOpen(true);
    };

    // Format date
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Get status badge class
    const getStatusBadgeClass = (status: string) => {
        switch (status.toLowerCase()) {
            case 'open':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
            case 'in progress':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
            case 'resolved':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'closed':
                return 'bg-neutral-100 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-200';
            default:
                return 'bg-neutral-100 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-200';
        }
    };

    return (
        <AdminLayout>
            <div className="p-6 dark:bg-neutral-900">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">Problèmes Management</h1>
                    <button
                        onClick={fetchProblems}
                        className="px-4 py-2 bg-neutral-200 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-200 rounded-lg hover:bg-neutral-300 dark:hover:bg-neutral-600 transition-colors flex items-center gap-2"
                    >
                        <RefreshCw size={18} />
                        Refresh
                    </button>
                </div>

                {/* Search and filter controls */}
                <div className="mb-6 flex flex-col md:flex-row gap-4">
                    <div className="relative flex-1">
                        <input
                            type="text"
                            placeholder="Search problems..."
                            className="w-full p-2 pl-10 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <span className="absolute left-3 top-2.5 text-neutral-400 dark:text-neutral-500">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </span>
                    </div>
                    <select 
                        className="p-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
                        onChange={(e) => {/* Add filter functionality */}}
                    >
                        <option value="">All Types</option>
                        <option value="technical">Technical</option>
                        <option value="financial">Financial</option>
                        <option value="other">Other</option>
                    </select>
                    <select 
                        className="p-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
                        onChange={(e) => {/* Add filter functionality */}}
                    >
                        <option value="">All Statuses</option>
                        <option value="open">Ouvert</option>
                        <option value="in progress">En cours</option>
                        <option value="resolved">Résolu</option>
                        <option value="closed">Fermé</option>
                    </select>
                </div>

                {loading ? (
                    <TableSkeleton />
                ) : (
                    <>
                        {problems.length === 0 ? (
                            <div className="text-center py-8 bg-neutral-50 dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
                                <p className="text-neutral-500 dark:text-neutral-400">Aucun problème trouvé</p>
                                <button 
                                    onClick={() => {/* Add functionality to create new problem */}}
                                    className="mt-4 px-4 py-2 bg-blue-600 text-white dark:bg-blue-700 dark:text-neutral-100 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
                                >
                                    Signaler un problème
                                </button>
                            </div>
                        ) : (
                            <div className="overflow-x-auto rounded-lg border border-neutral-200 dark:border-neutral-700">
                                <table className="min-w-full bg-white dark:bg-neutral-800">
                                    <thead>
                                        <tr className="bg-neutral-50 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600">
                                            <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">ID</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">User</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Description</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Type</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Status</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Date de signalement</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
                                        {problems.map((problem) => (
                                            <tr key={problem.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-700">
                                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-neutral-900 dark:text-neutral-100">#{problem.id}</td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        {problem.user.profile && (
                                                            <img 
                                                                src={`${basUrlStorage + problem.user.profile}`} 
                                                                alt={problem.user.name} 
                                                                className="w-8 h-8 rounded-full mr-2"
                                                            />
                                                        )}
                                                        <div>
                                                            <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{problem.user.name}</div>
                                                            <div className="text-xs text-neutral-500 dark:text-neutral-400">{problem.user.email}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-neutral-900 dark:text-neutral-200 max-w-[200px] truncate">
                                                    <div 
                                                        className="cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
                                                        onClick={() => handleViewDetails(problem)}
                                                    >
                                                        {problem.description}
                                                        {problem.description.length > 30 && 
                                                            <span className="text-blue-600 dark:text-blue-400 ml-1">[View more]</span>
                                                        }
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-neutral-900 dark:text-neutral-200 capitalize">{problem.type}</td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(problem.status)}`}>
                                                        {problem.status}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-neutral-900 dark:text-neutral-200">{formatDate(problem.created_at)}</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-neutral-500">
                                                    <div className="flex items-center gap-2">
                                                        <div className="relative">
                                                            <select
                                                                className="block appearance-none w-full bg-white dark:bg-neutral-700 border border-neutral-300 dark:border-neutral-600 hover:border-neutral-400 dark:hover:border-neutral-500 px-4 py-2 pr-8 rounded shadow leading-tight focus:outline-none focus:shadow-outline text-sm text-neutral-900 dark:text-neutral-100"
                                                                value={problem.status}
                                                                onChange={(e) => handleStatusChange(problem.id, e.target.value)}
                                                            >
                                                                <option value="open">Ouvert</option>
                                                                <option value="in progress">En cours</option>
                                                                <option value="resolved">Résolu</option>
                                                                <option value="closed">Fermé</option>
                                                            </select>
                                                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-neutral-700 dark:text-neutral-300">
                                                                <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                                                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                                                                </svg>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="mt-6">
                                <Pagination>
                                    <PaginationContent>
                                        <PaginationItem>
                                            <PaginationPrevious
                                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                                className={currentPage === 1 ? "pointer-events-none opacity-50" : ""}
                                            />
                                        </PaginationItem>
                                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                            <PaginationItem key={page}>
                                                <PaginationLink
                                                    onClick={() => setCurrentPage(page)}
                                                    isActive={currentPage === page}
                                                >
                                                    {page}
                                                </PaginationLink>
                                            </PaginationItem>
                                        ))}
                                        <PaginationItem>
                                            <PaginationNext
                                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                                className={currentPage === totalPages ? "pointer-events-none opacity-50" : ""}
                                            />
                                        </PaginationItem>
                                    </PaginationContent>
                                </Pagination>
                            </div>
                        )}
                    </>
                )}

                {/* Problem Details Modal */}
                <ProblemDetailsModal 
                    problem={selectedProblem} 
                    isOpen={isModalOpen} 
                    onClose={() => {
                        setIsModalOpen(false);
                        setTimeout(() => setSelectedProblem(null), 300); // Clear selected problem after modal close animation
                    }} 
                />
            </div>
        </AdminLayout>
    );
};